import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";

import { api } from "../lib/projects-api.ts";

/**
 * A project's settings: who may see it, its tag palette, its status, and —
 * last, separated by its own tab — deleting it outright. Access is the
 * point of Members: a project with members is private to them, members
 * editing and viewers reading, and an empty list means public. The dialog
 * never lets the last member be removed, matching the server.
 */
export default function ProjectSettings({ project, people, tags, me, onClose, onChanged, onDeleted }) {
  const [tab, setTab] = useState(0);
  const [members, setMembers] = useState(project.members || []);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const nameOf = (id) => {
    const p = people.find((x) => x.id === id);
    return p?.displayName || p?.emails?.[0] || (id === me ? "You" : id);
  };

  const saveMembers = async (next) => {
    setMembers(next);
    setError(null);
    try { await api.updateProject(project.id, { members: next }); onChanged(); }
    catch (e) { setError(e.body?.detail || e.message); }
  };

  const addPerson = (id) => {
    if (!id || members.some((m) => m.id === id)) return;
    saveMembers([...members, { id, name: nameOf(id), role: "member" }]);
  };
  const setRole = (id, role) => saveMembers(members.map((m) => m.id === id ? { ...m, role } : m));
  const remove = (id) => {
    const next = members.filter((m) => m.id !== id);
    if (next.length && !next.some((m) => m.role === "member")) {
      setError("A project keeps at least one member who can edit it.");
      return;
    }
    saveMembers(next);
  };

  const setFlag = (field) => async (value) => {
    setBusy(true);
    try { await api.updateProject(project.id, { [field]: value }); onChanged(); }
    finally { setBusy(false); }
  };

  const [deleting, setDeleting] = useState(false);
  const [confirmName, setConfirmName] = useState("");
  const del = async () => {
    setBusy(true);
    setError(null);
    try { await api.deleteProject(project.id); onDeleted(); }
    catch (e) { setError(e.body?.detail || e.message); setBusy(false); }
  };

  const candidates = people.filter((p) => !members.some((m) => m.id === p.id));

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Project settings</DialogTitle>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2 }}>
        <Tab label="Members" />
        <Tab label="Tags" />
        <Tab label="Status" />
        <Tab label="Danger" sx={{ color: "error.main" }} />
      </Tabs>
      <DialogContent>
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

        {tab === 0 ? (
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              {members.length
                ? "Private to these people. Members edit; viewers read."
                : "Public — everyone signed in can see and edit it. Add someone to restrict it."}
            </Typography>
            {members.map((m) => (
              <Stack key={m.id} direction="row" spacing={1} sx={{ alignItems: "center" }}>
                <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>{nameOf(m.id)}</Typography>
                <TextField select size="small" value={m.role} sx={{ width: 120 }}
                  onChange={(e) => setRole(m.id, e.target.value)}>
                  <MenuItem value="member">Member</MenuItem>
                  <MenuItem value="viewer">Viewer</MenuItem>
                </TextField>
                <IconButton size="small" aria-label={`Remove ${nameOf(m.id)}`} onClick={() => remove(m.id)}>
                  <Box component="span" sx={{ fontSize: 16, lineHeight: 1 }}>×</Box>
                </IconButton>
              </Stack>
            ))}
            <Divider />
            <TextField select size="small" label="Add a person" value=""
              onChange={(e) => addPerson(e.target.value)} disabled={!candidates.length}>
              {candidates.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.displayName || p.emails?.[0] || p.id}</MenuItem>
              ))}
            </TextField>
          </Stack>
        ) : null}

        {tab === 1 ? <TagPalette project={project} tags={tags} onChanged={onChanged} /> : null}

        {tab === 2 ? (
          <Stack spacing={2}>
            <Stack spacing={0.5}>
              <FormControlLabel
                control={<Switch checked={!!project.onHold} disabled={busy}
                  onChange={(e) => setFlag("onHold")(e.target.checked)} />}
                label="On hold" />
              <Typography variant="body2" color="text.secondary">
                Paused for now — a badge, nothing more. Doesn't move it off the
                project list or change who can see it.
              </Typography>
            </Stack>
            <Stack spacing={0.5}>
              <FormControlLabel
                control={<Switch checked={!!project.maintenanceMode} disabled={busy}
                  onChange={(e) => setFlag("maintenanceMode")(e.target.checked)} />}
                label="Maintenance" />
              <Typography variant="body2" color="text.secondary">
                Work is still happening, but sustaining rather than new delivery.
                Its own tab on the project list, so feature work stays the default view.
              </Typography>
            </Stack>
            <Stack spacing={0.5}>
              <FormControlLabel
                control={<Switch checked={!!project.internal} disabled={busy}
                  onChange={(e) => setFlag("internal")(e.target.checked)} />}
                label="Internal" />
              <Typography variant="body2" color="text.secondary">
                Not customer-facing — our own tooling. A separate tab on the
                project list, independent of maintenance.
              </Typography>
            </Stack>
            <Divider />
            <Stack spacing={0.5}>
              <FormControlLabel
                control={<Switch checked={!!project.archived} disabled={busy}
                  onChange={(e) => setFlag("archived")(e.target.checked)} />}
                label="Archived" />
              <Typography variant="body2" color="text.secondary">
                Drops out of the sidebar and the list unless you ask to see
                archived ones. Nothing is deleted.
              </Typography>
            </Stack>
          </Stack>
        ) : null}

        {tab === 3 ? (
          <Stack spacing={1.5}>
            <Typography variant="body2">
              This permanently removes <strong>{project.name}</strong> and every
              column, card, epic, tag, comment and follower under it. This
              cannot be undone.
            </Typography>
            {!deleting ? (
              <Button color="error" variant="outlined" onClick={() => setDeleting(true)}>
                Delete project…
              </Button>
            ) : (
              <Stack spacing={1.5}>
                <TextField size="small" label={`Type "${project.name}" to confirm`}
                  value={confirmName} onChange={(e) => setConfirmName(e.target.value)} autoFocus />
                <Stack direction="row" spacing={1}>
                  <Button color="error" variant="contained" disabled={busy || confirmName !== project.name}
                    onClick={del}>
                    {busy ? "Deleting…" : "Delete project"}
                  </Button>
                  <Button onClick={() => { setDeleting(false); setConfirmName(""); }}>Cancel</Button>
                </Stack>
              </Stack>
            )}
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Done</Button></DialogActions>
    </Dialog>
  );
}

function TagPalette({ project, tags, onChanged }) {
  const [name, setName] = useState("");
  const [color, setColor] = useState("");
  const add = async () => {
    if (!name.trim()) return;
    await api.createTag(project.id, { name: name.trim(), color: color.trim() || undefined });
    setName(""); setColor(""); onChanged();
  };
  const del = async (id) => { await api.deleteTag(project.id, id); onChanged(); };
  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary">
        The tags the board offers. A card can also coin a new one by typing it.
      </Typography>
      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
        {tags.map((t) => (
          <Chip key={t.id} label={t.name} onDelete={() => del(t.id)}
            variant="outlined" sx={{ borderColor: t.color || "divider" }} />
        ))}
        {!tags.length ? <Typography variant="body2" color="text.secondary">No tags yet.</Typography> : null}
      </Stack>
      <Divider />
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <TextField size="small" label="New tag" value={name} onChange={(e) => setName(e.target.value)} />
        <TextField size="small" label="Color" placeholder="#f00" value={color}
          onChange={(e) => setColor(e.target.value)} sx={{ width: 110 }} />
        <Button onClick={add} disabled={!name.trim()}>Add</Button>
      </Stack>
    </Stack>
  );
}
